// dispatch-media-handlers.ts — dispatch-table handlers for media transcription
// lifecycle events (16 canonical event names from mediaevent.BrokerStream).
//
// Each handler patches the "media.run.live-state" query key so useMediaJobEvents
// can read per-run streaming state without opening its own EventSource.
// Frame.data = full broker payload: {org_id, asset_id, run_id, event_name, ...}
import type { QueryClient } from "@tanstack/react-query";
import type { Frame } from "./types";
import { mediaLiveKeys, type MediaJobStreamState } from "./media-live-state";

export function numberOrZero(v: unknown): number {
  return typeof v === "number" && Number.isFinite(v) ? v : 0;
}

type LiveStateProducer = (prev: MediaJobStreamState) => MediaJobStreamState;

const LIVE_STATUS_RANK: Record<string, number> = {
  pending: 1,
  processing: 2,
  encoding: 3,
  transcribing: 4,
  refining: 5,
  done: 6,
  failed: 6,
};

function forwardLiveStatus(
  current: MediaJobStreamState["status"],
  next: MediaJobStreamState["status"],
): MediaJobStreamState["status"] {
  if (next === "done" || next === "failed") return next;
  if (current === "done" || current === "failed") return current;
  const currentRank = LIVE_STATUS_RANK[current] ?? 0;
  const nextRank = LIVE_STATUS_RANK[next] ?? 0;
  return nextRank >= currentRank ? next : current;
}

function forwardListStatus(current: string | undefined, next: string): string {
  if (!current) return next;
  if (next === "done" || next === "failed") return next;
  if (current === "done" || current === "failed") return current;
  const currentRank = LIVE_STATUS_RANK[current] ?? 0;
  const nextRank = LIVE_STATUS_RANK[next] ?? 0;
  return nextRank >= currentRank ? next : current;
}

export function patchLiveState(qc: QueryClient, runId: string, producer: LiveStateProducer) {
  const key = mediaLiveKeys.run(runId);
  const existing = qc.getQueryData<MediaJobStreamState>(key);
  if (existing) {
    qc.setQueryData(key, producer(existing));
  }
  // If no entry (hook not mounted for this run), no-op — observer isn't watching.
}

function runId(frame: Frame): string | null {
  const d = frame.data as Record<string, unknown> | null;
  return typeof d?.run_id === "string" ? d.run_id : null;
}

function assetId(frame: Frame): string | null {
  const d = frame.data as Record<string, unknown> | null;
  return typeof d?.asset_id === "string" ? d.asset_id : null;
}

function invalidateJobs(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: ["media", "jobs"] });
}

// Refresh the media list snapshot so chips drop their "AI processing" overlay
// once the worker finishes / fails / advances. We pair an immediate cache
// mutation (patchListRowStatus) with an invalidate so:
//   - The chip flips instantly off the SSE event — same UX guarantee the
//     per-run live-state overlay gives the drawer.
//   - The follow-up refetch reconciles any fields the patch couldn't touch
//     (cost_usd, parts_done, kb_doc_id) so the row stays accurate when the
//     user lingers on the page.
// Without the patch step, an invalidate-then-refetch race would sometimes
// leave the list painted with the prior snapshot for one render tick — the
// user-visible "badge wedged at Queued" symptom we keep chasing.
function invalidateLists(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: ["media", "list"] });
}

// patchListRow rewrites the latestRun pointer for the asset row matching
// assetId. Keying by asset_id (not run_id) is critical: on retry the BE
// inserts a new run row, and SSE events carry the NEW run_id while the FE
// list snapshot still holds the PRIOR run_id from its last refetch. Patching
// by run_id silently no-ops until the refetch lands — long enough for the
// row to stay stuck at "Queued" through a sub-second pipeline. Keying by
// asset_id closes the gap: we swap the brief's id+status atomically so the
// chip reflects the live run without waiting for the BE round-trip.
function patchListRow(
  qc: QueryClient,
  assetIdValue: string,
  runIdValue: string,
  nextStatus: string,
) {
  let matchedKeys = 0;
  let matchedRows = 0;
  qc.setQueriesData<MediaListSnapshot | undefined>({ queryKey: ["media", "list"] }, (snapshot) => {
    matchedKeys += 1;
    if (!snapshot?.data) return snapshot;
    let touched = false;
    const nextData = snapshot.data.map((row) => {
      if (row.id !== assetIdValue) return row;
      matchedRows += 1;
      const prev = row.latestRun ?? null;
      const status = prev?.id === runIdValue ? forwardListStatus(prev.status, nextStatus) : nextStatus;
      if (prev?.id === runIdValue && prev.status === status) return row;
      touched = true;
      return {
        ...row,
        latestRun: { ...(prev ?? {}), id: runIdValue, status },
      };
    });
    // Return the original reference when nothing changed so React Query's
    // structural-share path skips the unneeded subscriber notification.
    return touched ? { ...snapshot, data: nextData } : snapshot;
  });
  // Dev-only tracing so the user can paste the console output back when the
  // chip refuses to update. Production builds strip this branch entirely.
  if (import.meta.env.DEV) {
    console.debug(
      `[media-patch] asset=${assetIdValue.slice(0, 8)} run=${runIdValue.slice(0, 8)} → ${nextStatus} | listKeys=${matchedKeys} rowsHit=${matchedRows}`,
    );
  }
}

// MediaListSnapshot mirrors the relevant shape of mediaService.list — keeping
// it narrow lets this helper compile without dragging the entire MediaAssetDTO
// import here, and any extra fields the BE adds pass through untouched via
// spread. Update if the wire shape ever drops `latestRun.id` or `data`.
type MediaListSnapshot = {
  data?: Array<
    {
      id: string;
      kbDocId?: string | null;
      latestRun?: { id?: string; status?: string } | null;
    } & Record<string, unknown>
  >;
} & Record<string, unknown>;

type MediaDetailSnapshot = {
  data?: {
    id: string;
    kbDocId?: string | null;
  } & Record<string, unknown>;
} & Record<string, unknown>;

export function handleMediaAssetEvent(_frame: Frame, qc: QueryClient) {
  qc.invalidateQueries({ queryKey: ["media"] });
}

// A new run row was inserted (typical: user re-ran an asset). The list endpoint
// joins each row to its latest_run snapshot — without invalidation the row keeps
// the previous terminal status and useMediaRunLiveOverlay never enters its
// active window, so subsequent lifecycle events are silently ignored for that row.
export function handleMediaRunCreated(frame: Frame, qc: QueryClient) {
  // Swap the row's latestRun pointer to the freshly-inserted run so the
  // chip flips to "Queued" instantly on retry click — without this patch
  // the row still references the terminal prior run until refetch lands,
  // and subsequent run.started events also no-op until then.
  const id = runId(frame);
  const aid = assetId(frame);
  if (id && aid) patchListRow(qc, aid, id, "pending");
  invalidateLists(qc);
}

export function handleMediaKBIngest(frame: Frame, qc: QueryClient) {
  const aid = assetId(frame);
  const d = frame.data as Record<string, unknown> | null;
  const knowledgeId = typeof d?.knowledge_id === "string" ? d.knowledge_id : null;
  if (aid && knowledgeId) {
    qc.setQueriesData<MediaListSnapshot | undefined>(
      { queryKey: ["media", "list"] },
      (snapshot) => {
        if (!snapshot?.data) return snapshot;
        let touched = false;
        const nextData = snapshot.data.map((row) => {
          if (row.id !== aid) return row;
          if (row.kbDocId === knowledgeId) return row;
          touched = true;
          return { ...row, kbDocId: knowledgeId };
        });
        return touched ? { ...snapshot, data: nextData } : snapshot;
      },
    );
    qc.setQueriesData<MediaDetailSnapshot | undefined>(
      { queryKey: ["media", "detail"] },
      (snapshot) => {
        if (!snapshot?.data || snapshot.data.id !== aid) return snapshot;
        if (snapshot.data.kbDocId === knowledgeId) return snapshot;
        return { ...snapshot, data: { ...snapshot.data, kbDocId: knowledgeId } };
      },
    );
  }
  invalidateJobs(qc);
  invalidateLists(qc);
  qc.invalidateQueries({ queryKey: ["media", "detail"] });
}

export function handleMediaRunStarted(frame: Frame, qc: QueryClient) {
  const id = runId(frame);
  const aid = assetId(frame);
  if (!id) return;
  patchLiveState(qc, id, (s) => ({ ...s, status: forwardLiveStatus(s.status, "processing") }));
  // Mirror the live-state status into the list snapshot so the row's chip
  // flips Queued → Processing without waiting for the BE refetch round-trip.
  // The invalidate then reconciles any out-of-band fields (cost, parts).
  if (aid) patchListRow(qc, aid, id, "processing");
  invalidateLists(qc);
}

export function handleMediaRunEncodeStarted(frame: Frame, qc: QueryClient) {
  const id = runId(frame);
  if (!id) return;
  patchLiveState(qc, id, (s) => ({ ...s, status: forwardLiveStatus(s.status, "encoding") }));
}

export function handleMediaRunEncodeCompleted(frame: Frame, qc: QueryClient) {
  const id = runId(frame);
  if (!id) return;
  const d = frame.data as Record<string, unknown>;
  const total = numberOrZero(d.numParts ?? d.num_parts);
  patchLiveState(qc, id, (s) => ({
    ...s,
    status: forwardLiveStatus(s.status, "transcribing"),
    partsProgress: { done: s.partsProgress.done, total: total || s.partsProgress.total },
  }));
  // Defense-in-depth: even though BE run.status remains 'processing' across
  // intermediate phases, refetching here closes the gap if the terminal
  // event drops (broker hiccup, SSE disconnect) — the snapshot reflects
  // whatever DB state survives, never wedges at "Queued".
  invalidateLists(qc);
}

export function handleMediaRunPartProgress(frame: Frame, qc: QueryClient) {
  const id = runId(frame);
  if (!id) return;
  const d = frame.data as Record<string, unknown>;
  const total = numberOrZero(d.totalParts ?? d.total_parts);
  const done = numberOrZero(d.donePartCount ?? d.done_part_count);
  const cost = numberOrZero(d.cost_usd ?? d.costUsd);
  patchLiveState(qc, id, (s) => ({
    ...s,
    status: forwardLiveStatus(s.status, "transcribing"),
    partsProgress: {
      done: Math.max(s.partsProgress.done, done),
      total: total || s.partsProgress.total,
    },
    // Monotonic guard against out-of-order delivery — cost only grows
    // across the run. Undefined incoming (older BE without the key)
    // collapses to 0 here, so Math.max keeps the prior live value.
    costUsd: Math.max(s.costUsd ?? 0, cost),
  }));
}

export function handleMediaRunTranscribeStarted(frame: Frame, qc: QueryClient) {
  const id = runId(frame);
  if (!id) return;
  patchLiveState(qc, id, (s) => ({ ...s, status: forwardLiveStatus(s.status, "transcribing") }));
}

export function handleMediaRunTranscribeCompleted(frame: Frame, qc: QueryClient) {
  const id = runId(frame);
  if (!id) return;
  patchLiveState(qc, id, (s) => ({
    ...s,
    status: forwardLiveStatus(s.status, "refining"),
    partsProgress: { ...s.partsProgress, done: s.partsProgress.total || s.partsProgress.done },
  }));
  invalidateLists(qc);
}

export function handleMediaRunRefineStarted(frame: Frame, qc: QueryClient) {
  const id = runId(frame);
  if (!id) return;
  patchLiveState(qc, id, (s) => ({ ...s, status: forwardLiveStatus(s.status, "refining") }));
}

export function handleMediaRunRefineCompleted(frame: Frame, qc: QueryClient) {
  const id = runId(frame);
  if (!id) return;
  const d = frame.data as Record<string, unknown>;
  const wordCount = numberOrZero(d.wordCount ?? d.word_count);
  const cost = numberOrZero(d.cost_usd ?? d.costUsd);
  patchLiveState(qc, id, (s) => ({
    ...s,
    status: forwardLiveStatus(s.status, "refining"),
    wordCount,
    costUsd: Math.max(s.costUsd ?? 0, cost),
  }));
  invalidateLists(qc);
}

export function handleMediaRunCompleted(frame: Frame, qc: QueryClient) {
  const id = runId(frame);
  const aid = assetId(frame);
  if (!id) return;
  patchLiveState(qc, id, (s) => ({ ...s, status: "done" as const }));
  // Patch list snapshot first so the badge flips Ready immediately, then
  // invalidate so the follow-up BE refetch reconciles cost/word_count/kb_doc_id.
  if (aid) patchListRow(qc, aid, id, "done");
  invalidateJobs(qc);
  invalidateLists(qc);
}

export function handleMediaRunFailed(frame: Frame, qc: QueryClient) {
  const id = runId(frame);
  const aid = assetId(frame);
  if (!id) return;
  const d = frame.data as Record<string, unknown>;
  const error =
    typeof d.error === "string"
      ? d.error
      : typeof d.error_message === "string"
        ? d.error_message
        : undefined;
  patchLiveState(qc, id, (s) => ({
    ...s,
    status: "failed" as const,
    lastError: error ?? s.lastError,
  }));
  if (aid) patchListRow(qc, aid, id, "failed");
  invalidateJobs(qc);
  invalidateLists(qc);
}

export function handleMediaRunRetried(frame: Frame, qc: QueryClient) {
  const id = runId(frame);
  const aid = assetId(frame);
  if (!id) return;
  patchLiveState(qc, id, (s) => ({
    ...s,
    status: "pending" as const,
    lastError: undefined,
  }));
  if (aid) patchListRow(qc, aid, id, "pending");
  invalidateLists(qc);
}
